import { StatusCodes } from '../Libs/websocket-status-codes'
import { StatusCodes as HTTPStatusCodes } from '../Libs/http-status-codes'
import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRelogin } from './use-relogin'
import { stringifyError, useNewRelic } from '../Libs/observability-tool'
import { AuthContext } from '../Libs/authentication'

const TRY_AGAIN_LATER_RETRIES_AMOUNT = 3
const POLICY_VIOLATION_RETRIES_AMOUNT = 1

type Props<M, D> = {
  apiResourcePaths: string[]
  loggerKey: string
  transformer: (data: D) => M
  endpoint: string
  dependencies?: unknown[]
  doNotInitialize?: boolean
  keepAlive?: boolean
}

type AggregatedSocketState<M> = {
  connected: boolean
  error?: { code: number; reason?: string }
  data?: Record<string, M>
}

interface UseMultiWebSocketApiReturnType<M> {
  multiSocketState: AggregatedSocketState<M>
  webSockets: MutableRefObject<Record<string, WebSocket | null>>
  resetAllWebsockets: () => void
}

/**
 * A hook to connect to multiple websockets.
 *
 * Closes itself as soon as the components unmounts where the hook was called.
 * Reopens with reauthentication on jwt and websocket expiration codes according to https://docs.e3dc.com/x/J4ZmB
 *
 * @param {Props.apiResourcePaht} apiResourcePaths The to be called api resources
 * @param transformer A function to transform the incoming api resource object into a local domain model, recognized by the application
 * @param loggerKey Identifier for this websocket instance during the logging
 * @param endpoint The to be called api endpoint (default REACT_APP_API_ENDPOINT endpoint
 * @template M - Local domain model, recognized by the application
 * @template D - Incoming api resource object
 */
export const useMultiWebSocketApi = <M, D>({
  apiResourcePaths,
  loggerKey,
  transformer,
  endpoint,
  dependencies = [],
  doNotInitialize = false,
  keepAlive = false,
}: Props<M, D>): UseMultiWebSocketApiReturnType<M> => {
  const [multiSocketState, setMultiSocketState] = useState<AggregatedSocketState<M>>({
    connected: false,
    data: {},
  })
  const { authToken } = useContext(AuthContext)
  const webSockets = useRef<Record<string, WebSocket | null>>({})
  const retryCounters = useRef<Record<string, number>>({})
  const { pageActions } = useNewRelic()
  const relogin = useRelogin()

  const websocketEndpoint = endpoint.replace('https://', 'wss://').replace('http://', 'ws://')

  const setupWebSocket = useCallback(
    (path: string) => {
      if (!authToken) return

      const wsUrl = `${websocketEndpoint}/${path}?authorization=${authToken}`
      const ws = new WebSocket(wsUrl)
      webSockets.current[path] = ws

      ws.onopen = () => {
        setMultiSocketState(prev => ({
          ...prev,
          connected: Object.values(webSockets.current).every(socket => socket?.readyState === WebSocket.OPEN),
        }))
      }

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.statusCode === HTTPStatusCodes.NO_CONTENT && Object.keys(data).length === 1) return

          const model = transformer(data)

          setMultiSocketState(prev => ({
            ...prev,
            data: { ...prev.data, [path]: model },
          }))
        } catch (error) {
          console.error(`Websocket ${loggerKey}/${path} had an error parsing the message:`, error)
        }
      }

      ws.onerror = () => {
        setMultiSocketState(prev => ({
          ...prev,
          error: { code: StatusCodes.CLOSE_ABNORMAL },
        }))
        console.error(`Websocket ${loggerKey}/${path} had an unknown error.`)
      }

      ws.onclose = async event => {
        const errorHasChanged = multiSocketState.error?.code !== event.code
        console.log(`Websocket ${loggerKey}/${path} closed. Code: ${event.code}, Reason: ${event.reason}`)

        retryCounters.current[path] = retryCounters.current[path] || 0

        switch (event.code) {
          case StatusCodes.TRY_AGAIN_LATER: {
            if (retryCounters.current[path] < TRY_AGAIN_LATER_RETRIES_AMOUNT || errorHasChanged) {
              retryCounters.current[path] = errorHasChanged ? 1 : retryCounters.current[path] + 1
              setupWebSocket(path)
            }
            break
          }
          case StatusCodes.POLICY_VIOLATION: {
            if (retryCounters.current[path] < POLICY_VIOLATION_RETRIES_AMOUNT || errorHasChanged) {
              try {
                await relogin()
              } catch (err) {
                pageActions.logEvent({ logValue: `Relogin-Error on use-multi-websocket-api: ${stringifyError(err as Error)}` })
              }
              retryCounters.current[path] = errorHasChanged ? 1 : retryCounters.current[path] + 1
              setupWebSocket(path)
            }
            break
          }
          default:
            if (keepAlive) {
              retryCounters.current[path] = errorHasChanged ? 1 : retryCounters.current[path] + 1
              setupWebSocket(path)
            }
        }

        setMultiSocketState(prev => ({
          ...prev,
          connected: false,
          error: { code: event.code, reason: event.reason },
        }))
      }
    },
    [authToken, loggerKey, transformer, websocketEndpoint, multiSocketState.error?.code, keepAlive, relogin, pageActions]
  )

  useEffect(() => {
    if (doNotInitialize || !authToken) return

    apiResourcePaths.forEach(path => {
      setupWebSocket(path)
    })

    const sockets = webSockets.current

    return () => {
      Object.values(sockets).forEach(socket => {
        socket?.close()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResourcePaths, authToken, doNotInitialize, ...dependencies, setupWebSocket])

  const resetAllWebsockets = useCallback(() => {
    Object.keys(webSockets.current).forEach(path => {
      webSockets.current[path]?.close()
      webSockets.current[path] = null
    })
    setMultiSocketState({ connected: false, data: {} })
    retryCounters.current = {}
  }, [])

  return { multiSocketState, webSockets, resetAllWebsockets }
}
